/**
 * Public Team Bios Page
 *
 * Public-facing display of studio team member bios.
 * No authentication required - designed for embedding or public viewing.
 * Applies studio theme (colors, fonts) for branded experience.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useGetPublicTeamBiosQuery } from '../../api/teamBiosApi';
import Watermark from '../../components/common/Watermark';
import { shouldShowWatermark } from '../../utils/watermark';

const PublicTeamBiosPage: React.FC = () => {
  const { studioSlug } = useParams<{ studioSlug: string }>();

  const {
    data: teamBiosData,
    isLoading,
    error,
  } = useGetPublicTeamBiosQuery(studioSlug || '', {
    skip: !studioSlug,
  });

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  // Error state
  if (error || !teamBiosData) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">
          Unable to load team bios. Please check the URL and try again.
        </Alert>
      </Container>
    );
  }

  const { config, studio, members, theme } = teamBiosData;

  // Apply studio theme colors using correct nested structure
  const primaryColor = theme?.tokens?.colors?.primary || '#1976d2';
  const fontFamily =
    theme?.tokens?.typography?.fontFamily?.body || 'Arial, sans-serif';
  const textColor = theme?.tokens?.colors?.text?.primary || '#000000';
  const backgroundColor = theme?.tokens?.colors?.background || '#ffffff';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor,
        fontFamily,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* Back to External URL Button */}
        {config.external_url && (
          <Box mb={3}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              href={config.external_url}
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
                '&:hover': {
                  borderColor: primaryColor,
                  backgroundColor: `${primaryColor}10`,
                },
              }}
            >
              Back
            </Button>
          </Box>
        )}

        {/* Page Title */}
        <Box mb={4}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontFamily,
              color: textColor,
              fontWeight: 'bold',
              textAlign: 'left',
            }}
          >
            {config.title || `${studio.name} Team`}
          </Typography>
        </Box>

        {/* Team Members - Horizontal Cards in Single Column */}
        {members.length === 0 ? (
          <Alert severity="info">
            No team members to display at this time.
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {members.map(member => (
              <Card
                key={member.display_name}
                sx={{
                  backgroundColor,
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-start' },
                    gap: 3,
                    p: 3,
                  }}
                >
                  {/* Member Photo */}
                  <Avatar
                    src={member.photo_url || undefined}
                    sx={{
                      width: 120,
                      height: 120,
                      flexShrink: 0,
                      border: `3px solid ${primaryColor}`,
                    }}
                  >
                    {member.display_name.charAt(0)}
                  </Avatar>

                  {/* Member Info */}
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: { xs: 'center', sm: 'left' },
                    }}
                  >
                    {/* Member Name */}
                    <Typography
                      variant="h5"
                      component="h2"
                      gutterBottom
                      sx={{
                        fontFamily,
                        color: textColor,
                        fontWeight: 'bold',
                      }}
                    >
                      {member.display_name}
                    </Typography>

                    {/* Member Title */}
                    {member.title && (
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{
                          fontFamily,
                          color: textColor,
                          fontWeight: 500,
                          fontStyle: 'italic',
                          opacity: 0.8,
                        }}
                      >
                        {member.title}
                      </Typography>
                    )}

                    {/* Member Bio */}
                    {member.bio && (
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily,
                          color: textColor,
                          mt: 2,
                          lineHeight: 1.6,
                          opacity: 0.9,
                        }}
                      >
                        {member.bio}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Footer with Back Button (if external URL exists) */}
        {config.external_url && (
          <Box mt={6} textAlign="center">
            <Button
              variant="contained"
              href={config.external_url}
              sx={{
                backgroundColor: primaryColor,
                fontFamily,
                '&:hover': {
                  backgroundColor: `${primaryColor}dd`,
                },
              }}
            >
              Back
            </Button>
          </Box>
        )}

        {/* Watermark (for free tier only) */}
        <Watermark
          textColor={textColor}
          shouldDisplay={shouldShowWatermark(studio.owner_plan_slug)}
        />
      </Container>
    </Box>
  );
};

export default PublicTeamBiosPage;
